import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useStore } from '../context/StoreContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Upload, 
  FileSpreadsheet, 
  CreditCard,
  Loader2,
  Trash2,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const CreditCardsPage = () => {
  const { stores, selectedStore, setSelectedStore } = useStore();
  const { isAdmin } = useAuth();
  
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [creditCards, setCreditCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  
  // New credit card dialog - REMOVED: Now fetched from Store Setup

  useEffect(() => {
    if (selectedStore?.id) {
      setSelectedStoreId(selectedStore.id);
    }
  }, [selectedStore]);

  const handleStoreChange = (storeId) => {
    setSelectedStoreId(storeId);
    const store = stores.find(s => s.id === storeId);
    if (store) {
      setSelectedStore(store);
    }
  };

  useEffect(() => {
    if (selectedStoreId) {
      fetchCreditCards();
    }
  }, [selectedStoreId]);

  useEffect(() => {
    if (selectedCardId) {
      setTransactions([]);
      fetchTransactions();
    } else {
      setTransactions([]);
    }
  }, [selectedCardId]);

  const fetchCreditCards = () => {
    // Fetch credit cards from the store setup configuration
    const currentStore = stores.find(s => s.id === selectedStoreId);
    if (currentStore?.credit_cards && currentStore.credit_cards.length > 0) {
      // Map store credit cards to the format expected by the component
      const storeCards = currentStore.credit_cards.map((card, idx) => ({
        id: card.id || `card_${idx}`,
        card_name: card.card_name || card.name || 'Unknown Card',
        card_provider: card.card_provider || '',
        card_last_four: card.card_last_four || card.last_four || '',
        credit_limit: card.credit_limit || card.limit || 0,
        balance: card.balance || 0
      }));
      setCreditCards(storeCards);
      if (storeCards.length > 0 && !selectedCardId) {
        setSelectedCardId(storeCards[0].id);
      }
    } else {
      setCreditCards([]);
      setSelectedCardId('');
    }
  };

  const fetchTransactions = async () => {
    if (!selectedStoreId || !selectedCardId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/credit-cards/transactions`, {
        params: { 
          store_id: selectedStoreId,
          credit_card_id: selectedCardId
        }
      });
      setTransactions(response.data);
    } catch (error) {
      console.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!selectedStoreId) {
      toast.error('Please select a store first');
      return;
    }
    
    if (!selectedCardId) {
      toast.error('Please select or add a credit card first');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('store_id', selectedStoreId);
      formData.append('credit_card_id', selectedCardId);
      
      const response = await axios.post(`${API}/api/credit-cards/upload-statement`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`${response.data.imported} transactions imported (${response.data.duplicates} duplicates skipped). Format: ${response.data.format_detected}`);
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload statement');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) return;
    
    try {
      await axios.delete(`${API}/api/credit-cards/transactions/${transactionId}`);
      toast.success('Transaction deleted');
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete transaction');
    }
  };

  const handleClearAllTransactions = async () => {
    if (!selectedCardId) return;
    
    const cardName = creditCards.find(c => c.id === selectedCardId)?.card_name || 'this card';
    if (!window.confirm(`Are you sure you want to delete ALL non-reconciled transactions for ${cardName}? This cannot be undone.`)) return;
    
    try {
      const response = await axios.delete(`${API}/api/credit-cards/transactions/bulk/delete`, {
        data: {
          credit_card_id: selectedCardId,
          delete_all_for_card: true
        }
      });
      toast.success(`${response.data.deleted_count} transactions deleted`);
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to delete transactions');
    }
  };

  const handleRecleanDescriptions = async () => {
    if (!selectedCardId) return;
    
    try {
      const response = await axios.post(`${API}/api/credit-cards/transactions/reclean-descriptions`, {
        credit_card_id: selectedCardId
      });
      toast.success(`${response.data.updated_count} transaction descriptions cleaned`);
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to clean descriptions');
    }
  };

  const selectedCard = creditCards.find(c => c.id === selectedCardId);
  const totalTransactions = transactions.length;
  const reconciledCount = transactions.filter(t => t.is_reconciled).length;
  const unreconciledCount = totalTransactions - reconciledCount;

  return (
    <div className="space-y-6" data-testid="credit-cards-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CreditCard className="h-6 w-6" />
            Credit Card Statements
          </h1>
          <p className="text-muted-foreground">Upload and manage credit card transactions</p>
        </div>
        
        {isAdmin && (
          <Select value={selectedStoreId} onValueChange={handleStoreChange}>
            <SelectTrigger className="w-[200px]" data-testid="store-select">
              <SelectValue placeholder="Select Store" />
            </SelectTrigger>
            <SelectContent>
              {stores.map(store => (
                <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Credit Card Selection & Upload */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Credit Card Selection</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Credit Card Selector */}
            <div className="flex-1 min-w-[200px]">
              <Label>Credit Card</Label>
              <Select value={selectedCardId} onValueChange={setSelectedCardId} className="mt-1">
                <SelectTrigger data-testid="card-select">
                  <SelectValue placeholder={creditCards.length === 0 ? "No credit cards configured" : "Select Credit Card"} />
                </SelectTrigger>
                <SelectContent>
                  {creditCards.map(card => (
                    <SelectItem key={card.id} value={card.id}>
                      {card.card_name} {card.card_last_four && `(****${card.card_last_four})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {creditCards.length === 0 && selectedStoreId && (
                <p className="text-xs text-amber-500 mt-1">Configure credit cards in Store Setup</p>
              )}
            </div>

            {/* Upload Button */}
            <div>
              <Label className="invisible">Upload</Label>
              <div className="relative mt-1">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={!selectedCardId || uploading}
                  data-testid="file-upload"
                />
                <Button disabled={!selectedCardId || uploading}>
                  {uploading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Upload Statement
                </Button>
              </div>
            </div>
          </div>

          {/* Balance Display */}
          {selectedCard && (
            <div className="flex flex-wrap gap-4 p-4 bg-muted/50 rounded-lg">
              <div>
                <p className="text-xs text-muted-foreground">Credit Used</p>
                <p className="text-xl font-bold text-red-500">
                  -£{Math.abs(selectedCard.balance || 0).toFixed(2)}
                </p>
              </div>
              {selectedCard.credit_limit > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground">Credit Limit</p>
                  <p className="text-xl font-bold">£{selectedCard.credit_limit.toFixed(2)}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground">Available Credit</p>
                <p className="text-xl font-bold text-green-500">
                  £{((selectedCard.credit_limit || 0) - Math.abs(selectedCard.balance || 0)).toFixed(2)}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transactions List */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileSpreadsheet className="h-5 w-5" />
              Transactions ({totalTransactions})
              {unreconciledCount > 0 && (
                <span className="text-sm font-normal text-muted-foreground">
                  ({unreconciledCount} unreconciled)
                </span>
              )}
            </CardTitle>
            {transactions.length > 0 && (
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-blue-500 hover:text-blue-600 hover:bg-blue-500/10"
                  onClick={handleRecleanDescriptions}
                  data-testid="reclean-descriptions-btn"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Clean References
                </Button>
                {transactions.some(t => !t.is_reconciled) && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                    onClick={handleClearAllTransactions}
                    data-testid="clear-all-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Clear All
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : !selectedCardId ? (
            <div className="text-center py-12 text-muted-foreground">
              <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Select a credit card to view transactions</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No transactions found</p>
              <p className="text-sm">Upload a credit card statement to get started</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {transactions.map((txn) => (
                <div 
                  key={txn.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    txn.is_reconciled 
                      ? 'border-green-500/30 bg-green-500/5' 
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">
                        {txn.description || txn.merchant || 'Transaction'}
                      </span>
                      {txn.is_reconciled && (
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {txn.date}
                    </div>
                  </div>
                  <div className={`font-mono font-semibold ${
                    txn.amount > 0 ? 'text-red-500' : 'text-green-500'
                  }`}>
                    {txn.amount > 0 ? '-' : '+'}£{Math.abs(txn.amount).toFixed(2)}
                  </div>
                  {!txn.is_reconciled && (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDeleteTransaction(txn.id)}
                      className="text-muted-foreground hover:text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
};

export default CreditCardsPage;
